// Shared persistence client for the Mass Balance utilities (both tabs). Stores raw uploads to S3
// + an index in DynamoDB via /mass-balance/* (see MASS_BALANCE_ENHANCEMENT_PLAN.md P2b).
// Dedup is by SHA-256 content hash (computed here in the browser, so a byte-identical re-upload
// is rejected server-side and never counted twice).
const API = process.env.REACT_APP_API_ENDPOINT || '';
const auth = (idToken?: string): Record<string, string> => (idToken ? { Authorization: `Bearer ${idToken}` } : {});

export interface StoredFile {
  uploadId: string; filename: string; kind: string; period: string; size: number;
  content_hash: string; uploaded_at: string; uploaded_by: string; url: string | null;
}
export interface PersistItem { file: File; kind: string; }
// Imperative handle each Mass Balance tab exposes so the shared hub uploader can hand it the files
// that belong to that tab's format family (it runs the tab's own parser + persist path).
export interface MBTabHandle { ingest: (files: File[]) => Promise<void>; }
export interface PersistResult { filename: string; status: 'stored' | 'duplicate' | 'error'; existing_period?: string; }

// SHA-256 hex of a file's bytes (browser SubtleCrypto).
export async function hashFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Hash + POST for dedup/index + PUT the raw bytes for the new (non-duplicate) files.
// Ask the backend to recompute a dataset's analytics snapshot (after upload bytes land). Fire-and-forget.
export function triggerRecompute(dataset: string, period?: string, idToken?: string): void {
  try {
    fetch(`${API}/mass-balance/recompute`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...auth(idToken) },
      body: JSON.stringify({ dataset, period }),
    }).catch(() => { /* best-effort */ });
  } catch { /* ignore */ }
}

export async function persistUploads(utility: string, dataset: string, period: string, items: PersistItem[], idToken?: string): Promise<PersistResult[]> {
  try {
    const withHash = await Promise.all(items.map(async (it) => ({ it, hash: await hashFile(it.file) })));
    const body = {
      utility, dataset, period,
      files: withHash.map(({ it, hash }) => ({ filename: it.file.name, kind: it.kind, content_hash: hash, size: it.file.size })),
    };
    const res = await fetch(`${API}/mass-balance/uploads`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...auth(idToken) }, body: JSON.stringify(body),
    });
    if (!res.ok) return items.map((it) => ({ filename: it.file.name, status: 'error' as const }));
    const data = await res.json();
    const results: any[] = data.results || [];
    await Promise.all(results.map(async (r) => {
      if (r.status === 'stored' && r.put_url) {
        const src = withHash.find((w) => w.it.file.name === r.filename);
        if (src) { try { await fetch(r.put_url, { method: 'PUT', body: src.it.file }); } catch { /* best-effort */ } }
      }
    }));
    // Bytes are now in S3 → recompute this dataset's analytics so Cal + the UI stay current.
    if (results.some((r) => r.status === 'stored')) triggerRecompute(dataset, period, idToken);
    return results.map((r) => ({ filename: r.filename, status: r.status, existing_period: r.existing_period }));
  } catch {
    return items.map((it) => ({ filename: it.file.name, status: 'error' as const }));
  }
}

// Thrown when a list/read call comes back 401/403 — i.e. the auth token has expired. Callers catch
// this to show "session expired, refresh to sign in" instead of a misleading empty list (an expired
// token otherwise reads as "you have no saved uploads", which looks like the data was deleted).
export class MBAuthError extends Error {
  constructor() { super('mass-balance-auth-expired'); this.name = 'MBAuthError'; }
}

export async function listPeriods(utility: string, dataset: string, idToken?: string): Promise<string[]> {
  let res: Response;
  try {
    res = await fetch(`${API}/mass-balance/periods?utility=${utility}&dataset=${encodeURIComponent(dataset)}`, { headers: auth(idToken) });
  } catch { return []; } // network/CORS error — treat as empty, not an auth problem
  if (res.status === 401 || res.status === 403) throw new MBAuthError();
  if (!res.ok) return [];
  return (await res.json()).periods || [];
}

export async function listUploads(utility: string, dataset: string, period: string, idToken?: string): Promise<StoredFile[]> {
  // Note: listPeriods (called first in every flow) is the auth-expiry detector; this stays
  // silent-empty so callers don't each need a try/catch.
  try {
    const res = await fetch(`${API}/mass-balance/uploads?utility=${utility}&dataset=${encodeURIComponent(dataset)}&period=${encodeURIComponent(period)}`, { headers: auth(idToken) });
    if (!res.ok) return [];
    return (await res.json()).files || [];
  } catch { return []; }
}

export async function deleteUpload(utility: string, dataset: string, period: string, uploadId: string, idToken?: string): Promise<boolean> {
  try {
    const res = await fetch(`${API}/mass-balance/uploads?utility=${utility}&dataset=${encodeURIComponent(dataset)}&period=${encodeURIComponent(period)}&uploadId=${uploadId}`, { method: 'DELETE', headers: auth(idToken) });
    return res.ok;
  } catch { return false; }
}

// Datasets = named customer/site bundles. Enumerated for the shared selector.
export interface DatasetInfo { name: string; oas: number; refinery: number; }
export async function listDatasets(idToken?: string): Promise<DatasetInfo[]> {
  try {
    const res = await fetch(`${API}/mass-balance/datasets`, { headers: auth(idToken) });
    if (res.status === 401 || res.status === 403) throw new MBAuthError();
    if (!res.ok) return [];
    return (await res.json()).datasets || [];
  } catch (e) {
    if (e instanceof MBAuthError) throw e;
    return [];
  }
}

// Persist the frontend-computed analytics snapshot (KPIs, In/Out balance, loss, adjustment, slate,
// per-grade rows) so Cal reads the SAME numbers the user sees. Best-effort (never blocks the UI).
// `utility` here = the TAB whose analytics these are: 'refinery' (balance) or 'oas' (diagnostics).
export async function persistAnalytics(dataset: string, utility: string, period: string, snapshot: any, mappingVersion?: string, idToken?: string): Promise<boolean> {
  try {
    const res = await fetch(`${API}/mass-balance/analytics`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...auth(idToken) },
      body: JSON.stringify({ dataset, utility, period, snapshot, mappingVersion }),
    });
    return res.ok;
  } catch { return false; }
}

/**
 * One period's stored analytics snapshot — the SAME per-grade balance the Mass Balance tables show.
 *
 * Switching months used to re-download and re-parse that month's workbooks every single time: three
 * files totalling 3.5–5.3 MB on EET, with no cache on the single-month path. The snapshot is ~45 KB
 * (about 80× smaller) and the balance rebuilt from it is byte-identical — verified across five
 * periods on both bases, worst difference 0.0.
 *
 * Returns null when a period has no snapshot, so the caller falls back to the workbook path.
 */
export async function fetchPeriodSnapshot(
  dataset: string, period: string, utility = 'refinery', idToken?: string,
): Promise<any | null> {
  try {
    const q = new URLSearchParams({ dataset, utility, period });
    const res = await fetch(`${API}/mass-balance/analytics?${q}`, { headers: auth(idToken) });
    if (res.status === 401 || res.status === 403) throw new MBAuthError();
    if (!res.ok) return null;                       // 404 = not computed yet
    const data = await res.json();
    const snap = data?.snapshot;
    return (snap && Array.isArray(snap.grades) && snap.grades.length) ? snap : null;
  } catch (e) {
    if (e instanceof MBAuthError) throw e;
    return null;
  }
}

/** Default loss margin when a dataset has none configured: ±0.50% of crude. A real target rather
 *  than a statistic, so a new dataset is judged against something meaningful from the first upload
 *  instead of against its own (possibly poor) history. Override per dataset in Settings. */
export const DEFAULT_MB_MARGIN_PCT = 0.5;

/** Per-dataset Mass Balance settings. Today just the loss margin, which decides what counts as
 *  "within margin" on the Mass Gain/Loss chart. PER DATASET on purpose: DS1 and DS2 are different
 *  refineries with different normal ranges, so a single silo-wide number would be wrong for one. */
export interface MbSettings {
  /** Loss margin as a PERCENT of crude, e.g. 0.5 for +/-0.5%. Null/absent = not configured. */
  marginPct?: number | null;
}

export async function fetchMbSettings(dataset: string, idToken?: string): Promise<MbSettings | null> {
  try {
    const q = new URLSearchParams({ type: 'settings', dataset });
    const res = await fetch(`${API}/mass-balance-mapping?${q}`, { headers: auth(idToken) });
    if (res.status === 401 || res.status === 403) throw new MBAuthError();
    if (!res.ok) return null;
    const data = await res.json();
    return (data?.mapping && typeof data.mapping === 'object') ? data.mapping as MbSettings : null;
  } catch (e) {
    if (e instanceof MBAuthError) throw e;
    return null;                       // never block the chart on a missing setting
  }
}

export async function saveMbSettings(dataset: string, settings: MbSettings, idToken?: string): Promise<boolean> {
  try {
    const q = new URLSearchParams({ type: 'settings', dataset });
    const res = await fetch(`${API}/mass-balance-mapping?${q}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...auth(idToken) },
      body: JSON.stringify({ mapping: settings }),
    });
    return res.ok;
  } catch { return false; }
}

// Download a stored file's bytes back into a File (for re-parsing with the existing parsers).
export async function fetchStoredFile(sf: StoredFile): Promise<File | null> {
  if (!sf.url) return null;
  try {
    const res = await fetch(sf.url);
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    return new File([buf], sf.filename);
  } catch { return null; }
}

// Modal YYYY-MM from a set of YYYY-MM-DD date keys (period auto-detect from the data).
export function modalPeriod(dateKeys: string[]): string {
  const counts: Record<string, number> = {};
  for (const d of dateKeys) { const m = (d || '').slice(0, 7); if (/^\d{4}-\d{2}$/.test(m)) counts[m] = (counts[m] || 0) + 1; }
  const entries = Object.entries(counts);
  if (!entries.length) return 'unknown';
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

// ---------------------------------------------------------------------------------------------
// Month End Checklist (P4) — the eleven checks a mass-balance analyst runs to close a month.
// Computed server-side in CalAgentActions from the analytics snapshot, so the checklist, Cal and
// the screen all read the same numbers. See get_month_end_checklist in agent_actions.py.
// ---------------------------------------------------------------------------------------------
export type ChecklistStatus = 'fail' | 'warn' | 'ok' | 'no_data';

export interface ChecklistItem {
  id: string;
  group: string;
  label: string;
  status: ChecklistStatus;
  headline: string;
  /** The rows behind the headline. Shape varies per check, so it is rendered generically. */
  findings: Record<string, any>[];
  /** Total findings before the server's cap — findings[] may be shorter than this. */
  finding_count: number;
  /** The question this item seeds into the chat when the user wants to discuss it. */
  follow_up: string;
}

export interface MonthEndChecklist {
  dataset: string; period: string; prior_period: string | null; utility: string;
  units: { volume: string; mass: string }; density_unit: string;
  groups: string[]; items: ChecklistItem[];
  summary: Record<ChecklistStatus, number>;
  needs_attention: { id: string; label: string; status: ChecklistStatus; headline: string }[];
  verdict: string;
}

export async function fetchMonthEndChecklist(
  dataset: string, period: string, utility: string, idToken?: string,
): Promise<MonthEndChecklist> {
  const q = new URLSearchParams({ dataset, period, utility });
  const res = await fetch(`${API}/mass-balance/checklist?${q}`, { headers: auth(idToken) });
  if (res.status === 401 || res.status === 403) throw new MBAuthError();
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Checklist failed (${res.status})`);
  }
  return res.json();
}

/** Cal's written overview of the close, plus a short summary per check (keyed by item id). */
export interface ChecklistNarrative {
  overview: string;
  items: Record<string, string>;
  /** true when the model ignored the requested structure — overview survives, per-item lines do not. */
  degraded?: boolean;
  model?: string;
}

/**
 * One direct model call (no agent, no conversation session) that summarises an already-computed
 * checklist. Passing the checklist back avoids recomputing it server-side and guarantees the prose
 * describes exactly what is on screen.
 *
 * Deliberately NOT allowed to fail the checklist: callers treat a rejection as "no prose this time".
 */
export async function fetchChecklistNarrative(
  dataset: string, period: string, utility: string, checklist: MonthEndChecklist, idToken?: string,
): Promise<ChecklistNarrative> {
  const res = await fetch(`${API}/mass-balance/checklist/narrative`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth(idToken) },
    body: JSON.stringify({ dataset, period, utility, checklist }),
  });
  if (res.status === 401 || res.status === 403) throw new MBAuthError();
  if (!res.ok) throw new Error(`Summary failed (${res.status})`);
  const body = await res.json();
  if (body.error) throw new Error(body.error);
  return { overview: body.overview || '', items: body.items || {}, degraded: body.degraded, model: body.model };
}

/** One period's PHYSICAL loss, from the site's own fuel & loss accounting. */
export interface LossPoint {
  period: string;
  /** % of crude. Null when the period has no fuel & loss source. Negative = a gain. */
  loss_pct: number | null;
  unaccounted_loss: number | null;
  fuel_pct: number | null;
  flare: number | null;
  source: string | null;
  /** Denominator for loss_pct — lets the UI weight monthly loss when rolling up to quarter/annual. */
  crude_processed: number | null;
  /** BS&W for the period, where the site reports it. */
  bsw?: number | null;
  /** The site's own restated monthly fuel figure. Deliberately NOT split: the trend column does not
   *  consistently include imported natural gas, so any own-fuel/NG split derived from it is wrong
   *  roughly one month in three. */
  fuel?: number | null;
  /** Imported natural gas for the period, from its own sheet. Reported ALONGSIDE `fuel`, not as a
   *  component to add to it — it is already inside the site's fuel figure in most months. */
  natural_gas?: number | null;
  /** Stock-ledger closure check — nets to ~0 by construction. NOT the physical loss.
   *  Only present on the fallback path (a dataset with no monthly trend table). */
  ledger_residual?: number | null;
  ledger_unit?: string | null;
  feed?: number;
}

export interface LossSeries {
  dataset: string; utility: string;
  units: { volume?: string; mass?: string };
  periods: LossPoint[];
  period_count: number;
  periods_with_real_loss: number;
  /** 'fuel_and_loss' when real loss is available for at least one period, else 'ledger_only'. */
  basis: 'fuel_and_loss' | 'ledger_only';
  /** Where the figures came from. monthly_trend is the site's restated table and is authoritative;
   *  per_period_snapshot is the fallback and can disagree for older months. */
  series_source?: 'monthly_trend' | 'per_period_snapshot';
}

/** One grade's legs within a trend point. `category` is the PRODUCT MAPPING sub-category
 *  ('feedstock-crude', 'products-gasoline', ...) — not BalanceRow's 'crude' | 'product'. The
 *  distinction matters: netProduction excludes feedstocks by testing for the 'feedstock' prefix. */
export interface TrendGrade {
  production: number; gainLoss: number; receipts: number; shipments: number;
  category: string; subcat: string;
}
export interface TrendPoint {
  key: string;
  opening: number; receipts: number; production: number;
  shipments: number; reclass: number; gainLoss: number;
  grade_count: number;
  byProduct: Record<string, TrendGrade>;
}
export interface TrendSeries {
  dataset: string;
  utility: string;
  basis: 'volume' | 'mass';
  units: { volume?: string; mass?: string };
  series: TrendPoint[];
  period_count: number;
  note?: string;
}

/**
 * Per-period balance totals + per-grade detail for Trends & analytics, in ONE request.
 *
 * Trends used to build this in the browser: list every period, download every stored workbook and
 * re-parse it with SheetJS. On EET that was 13 listUploads, 39 downloads, 51.9 MB and 39 XLSX
 * parses to draw one chart. The analytics snapshots already hold the identical per-grade balance,
 * so this reads them server-side — ~1 s and a few KB per period.
 *
 * Returns null on any failure so the caller can fall back to the client-side path.
 */
export async function fetchTrendSeries(
  dataset: string, basis: 'volume' | 'mass' = 'volume', utility = 'refinery', idToken?: string,
): Promise<TrendSeries | null> {
  try {
    const q = new URLSearchParams({ dataset, utility, basis });
    const res = await fetch(`${API}/mass-balance/trend-series?${q}`, { headers: auth(idToken) });
    if (res.status === 401 || res.status === 403) throw new MBAuthError();
    if (!res.ok) return null;
    const data = await res.json();
    return (data && Array.isArray(data.series) && data.series.length) ? data : null;
  } catch (e) {
    if (e instanceof MBAuthError) throw e;
    return null;
  }
}

/**
 * Per-period physical loss for Trends & analytics, in ONE request.
 *
 * Trends builds its series client-side from re-parsed workbooks, which never carry fuel & loss, so
 * it had been charting the stock-ledger residual — ~0 by construction, which is why every period
 * read -0.000%. Fetched server-side rather than pulling N snapshots into the browser: snapshots
 * spill to S3 above 350 KB, so drawing one line would have cost megabytes.
 */
export async function fetchLossSeries(
  dataset: string, utility = 'refinery', idToken?: string,
): Promise<LossSeries | null> {
  try {
    const q = new URLSearchParams({ dataset, utility });
    const res = await fetch(`${API}/mass-balance/loss-series?${q}`, { headers: auth(idToken) });
    if (res.status === 401 || res.status === 403) throw new MBAuthError();
    if (!res.ok) return null;          // optional decoration: never block the trends view
    return await res.json();
  } catch (e) {
    if (e instanceof MBAuthError) throw e;
    return null;
  }
}
